import { useState } from 'react';
import { Heart, Reply, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Comment {
  id: string;
  user_name: string;
  user_email: string;
  comment_text: string;
  likes: number;
  admin_reply: string | null;
  created_at: string;
  parent_id: string | null;
  replies?: Comment[];
}

interface CommentItemProps {
  comment: Comment;
  postId: string;
  onCommentAdded: () => void;
  level?: number;
}

const CommentItem = ({ comment, postId, onCommentAdded, level = 0 }: CommentItemProps) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showReplies, setShowReplies] = useState(true);
  const [replyName, setReplyName] = useState('');
  const [replyEmail, setReplyEmail] = useState('');
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const { toast } = useToast();

  const isNested = level > 0;
  const hasReplies = comment.replies && comment.replies.length > 0;

  // Generate a session ID for like tracking
  const getSessionId = () => {
    let sessionId = localStorage.getItem('comment_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('comment_session_id', sessionId);
    }
    return sessionId;
  };

  // Fetch like count and check if user has liked
  const fetchLikeStatus = async () => {
    try {
      const { count } = await supabase
        .from('comment_likes')
        .select('*', { count: 'exact', head: true })
        .eq('comment_id', comment.id);

      setLikeCount(count || 0);

      const sessionId = getSessionId();
      const { data } = await supabase
        .from('comment_likes')
        .select('id')
        .eq('comment_id', comment.id)
        .eq('user_session', sessionId)
        .maybeSingle();

      setHasLiked(!!data);
    } catch (error) {
      console.error('Error fetching like status:', error);
    }
  };

  useState(() => {
    fetchLikeStatus();
  });

  const handleLike = async () => {
    if (hasLiked) {
      toast({
        title: 'Already liked',
        description: 'You have already liked this comment',
        variant: 'destructive',
      });
      return;
    }

    try {
      const sessionId = getSessionId();
      const { error } = await supabase
        .from('comment_likes')
        .insert({
          comment_id: comment.id,
          user_session: sessionId,
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          toast({
            title: 'Already liked',
            description: 'You have already liked this comment',
            variant: 'destructive',
          });
        } else {
          throw error;
        }
        return;
      }

      setHasLiked(true);
      setLikeCount(prev => prev + 1);
      
      toast({
        title: 'Success',
        description: 'Comment liked!',
      });
    } catch (error) {
      console.error('Error liking comment:', error);
      toast({
        title: 'Error',
        description: 'Failed to like comment',
        variant: 'destructive',
      });
    }
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!replyName.trim() || !replyEmail.trim() || !replyText.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in all fields',
        variant: 'destructive',
      });
      return;
    }

    if (!replyEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('comments')
        .insert({
          post_id: postId,
          parent_id: comment.id,
          user_name: replyName.trim(),
          user_email: replyEmail.trim(),
          comment_text: replyText.trim(),
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Your reply has been posted!',
      });

      setReplyName('');
      setReplyEmail('');
      setReplyText('');
      setShowReplyForm(false);
      onCommentAdded();
    } catch (error) {
      console.error('Error posting reply:', error);
      toast({
        title: 'Error',
        description: 'Failed to post reply. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${isNested ? 'ml-4 sm:ml-8' : ''}`}>
      <div className="glass-morphism rounded-xl p-3 sm:p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h4 className="font-semibold text-sm text-foreground">{comment.user_name}</h4>
            <p className="text-xs text-muted-foreground">
              {new Date(comment.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </p>
          </div>
        </div>
        
        <p className="text-sm text-foreground/90 leading-relaxed mb-3">{comment.comment_text}</p>
        
        {comment.admin_reply && (
          <div className="mt-3 pl-3 border-l-2 border-primary bg-primary/5 rounded-lg p-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Shield className="w-3.5 h-3.5 text-primary" />
              <span className="text-xs font-semibold text-primary">Admin</span>
            </div>
            <p className="text-sm text-foreground/80">{comment.admin_reply}</p>
          </div>
        )}

        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleLike}
            disabled={hasLiked}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all text-xs sm:text-sm ${
              hasLiked
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                : 'glass-morphism hover:bg-muted text-muted-foreground'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${hasLiked ? 'fill-current' : ''}`} />
            <span className="font-medium">{likeCount}</span>
          </button>

          {level < 3 && (
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg glass-morphism hover:bg-muted text-muted-foreground transition-all text-xs sm:text-sm"
            >
              <Reply className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="font-medium">Reply</span>
            </button>
          )}
        </div>

        {showReplyForm && (
          <div className="mt-3 p-3 glass-morphism rounded-lg">
            <h5 className="text-sm font-semibold mb-3">Reply to {comment.user_name}</h5>
            <form onSubmit={handleReplySubmit} className="space-y-2.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <Input
                  type="text"
                  placeholder="Your Name *"
                  value={replyName}
                  onChange={(e) => setReplyName(e.target.value)}
                  maxLength={100}
                  required
                  className="bg-background/50 text-sm h-9"
                />
                <Input
                  type="email"
                  placeholder="Your Email *"
                  value={replyEmail}
                  onChange={(e) => setReplyEmail(e.target.value)}
                  maxLength={255}
                  required
                  className="bg-background/50 text-sm h-9"
                />
              </div>
              <Textarea
                placeholder="Your Reply *"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                maxLength={1000}
                required
                className="min-h-[70px] bg-background/50 text-sm"
              />
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90 text-xs h-8"
                  size="sm"
                >
                  {loading ? 'Posting...' : 'Post'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowReplyForm(false)}
                  size="sm"
                  className="text-xs h-8"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}
      </div>

      {hasReplies && (
        <div className="mt-3 space-y-3">
          <button
            onClick={() => setShowReplies(!showReplies)}
            className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-primary font-medium transition-colors ml-4 sm:ml-8"
          >
            {showReplies ? (
              <>
                <ChevronUp className="w-3.5 h-3.5" />
                Hide {comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5" />
                Show {comment.replies!.length} {comment.replies!.length === 1 ? 'reply' : 'replies'}
              </>
            )}
          </button>

          {showReplies && (
            <div className="space-y-3">
              {comment.replies!.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  postId={postId}
                  onCommentAdded={onCommentAdded}
                  level={level + 1}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentItem;
